import { useEffect, useRef, useState } from 'react';
import { RATE_LIMITED_EVENT } from '@/lib/formSubmissions';

/**
 * Global, app-root modal shown whenever any form is rejected with HTTP 429.
 * It listens for the `cendien:rate-limited` window event dispatched by
 * `submitForm`, so it works for every form without per-form wiring.
 */
export function RateLimitModal() {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onRateLimited = () => setOpen(true);
    window.addEventListener(RATE_LIMITED_EVENT, onRateLimited);
    return () => window.removeEventListener(RATE_LIMITED_EVENT, onRateLimited);
  }, []);

  // Accessible dialog behaviour: focus in, trap focus, Escape closes, lock scroll.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;

    const getFocusable = () =>
      Array.from(
        dialog?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') || []
      ).filter((el) => el.offsetParent !== null);

    (getFocusable()[0] || dialog)?.focus();

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setOpen(false);
        return;
      }
      if (ev.key === 'Tab' && dialog) {
        const focusables = getFocusable();
        if (focusables.length === 0) {
          ev.preventDefault();
          dialog.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (ev.shiftKey && document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></div>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rate-limit-title"
        tabIndex={-1}
        className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden outline-none"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-800 px-7 pt-7 pb-6">
          <div className="w-12 h-12 flex items-center justify-center bg-white/15 rounded-xl mb-4">
            <i className="ri-time-line text-white text-2xl"></i>
          </div>
          <h2 id="rate-limit-title" className="text-xl font-bold text-white leading-snug">
            Looks like you&rsquo;ve sent a few requests
          </h2>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          <p className="text-slate-600 text-sm leading-relaxed mb-6">
            To keep our forms spam-free we limit how many submissions come from one connection. The fastest way to reach us right now is a quick call.
          </p>

          <div className="flex flex-col gap-3">
            <a
              href="tel:+12142454580"
              className="whitespace-nowrap w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm rounded-xl py-3.5 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-base"></i>
              Call (214) 245-4580
            </a>
            <a
              href="mailto:leads@cendien.com"
              className="whitespace-nowrap w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-teal-300 hover:text-teal-700 text-slate-700 font-semibold text-sm rounded-xl py-3.5 transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-base"></i>
              Email leads@cendien.com
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-5 mx-auto block text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
          >
            Try again in a few minutes
          </button>
        </div>
      </div>
    </div>
  );
}

export default RateLimitModal;